/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'
import {useIntl, FormattedMessage, FormattedNumber} from 'react-intl'
import {Flex, Stack, Text, Box} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useItemVariant} from '@salesforce/retail-react-app/app/components/item-variant'
import PromoPopover from '@salesforce/retail-react-app/app/components/promo-popover'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {usePromotions, useProducts, useProduct} from '@salesforce/commerce-sdk-react'
import {getDisplayVariationValues} from '@salesforce/retail-react-app/app/utils/product-utils'
import colors from '../../theme/foundations/colors'
import {pxToRem} from '../../utills/pxToRem'
import {variantValues} from './index.style'
import useSizeMappingData from '../../utills/getProductSizeMapping'
import useSitePreference from '../../hooks/use-site-preference'
import {useDerivedProduct} from '../../hooks/use-derived-product'

/**
 * In the context of a cart product item variant, this component renders a styled
 * list of the selected variation values as well as any promos (w/ info popover).
 */
const ItemAttributes = ({
    includeQuantity,
    product,
    currency,
    isAccountPage = false,
    quantity,
    ...props
}) => {
    const variant = useItemVariant()
    const {data: basket} = useCurrentBasket()
    const {currency: activeCurrency} = useCurrency()
    const promotionIds = variant.priceAdjustments?.map((adj) => adj.promotionId) ?? []
    const intl = useIntl()
    const sizeMappingData = useSizeMappingData()

    // Fetch all the promotions given by price adjustments. We display this info in
    // the promotion info popover when applicable.
    const {data: res} = usePromotions(
        {
            parameters: {
                ids: promotionIds.join(',')
            }
        },
        {
            enabled: promotionIds.length > 0
        }
    )
    const promos = res?.data || []
    const variationValues = getDisplayVariationValues(
        variant?.variationAttributes,
        variant?.variationValues
    )

    // get variant info for bundled products in cart page and order history page
    const productBundleIds =
        variant?.bundledProductItems?.map(({productId}) => productId).join(',') ?? ''
    const {data: productBundleVariantData, isLoading: bundleVariantIsLoading} = useProducts(
        {
            parameters: {
                ids: productBundleIds,
                allImages: false
            }
        },
        {
            enabled: Boolean(variant?.type?.bundle && productBundleIds),
            select: (result) => {
                // formats response so we can easily display child quantity/variant selection
                return result?.data?.map((item) => {
                    const quantity = variant?.bundledProductItems.find(
                        (childProduct) => childProduct.productId === item.id
                    )?.quantity
                    return {
                        ...item,
                        quantity,
                        variationValues: getDisplayVariationValues(
                            item?.variationAttributes,
                            item?.variationValues
                        )
                    }
                })
            }
        }
    )

    // get bundle product data for wishlist page
    const {data: productBundleData, isLoading: productBundleIsLoading} = useProduct(
        {
            parameters: {
                id: variant?.id,
                allImages: false
            }
        },
        {
            enabled: Boolean(variant?.type?.bundle && !productBundleIds)
        }
    )

    const isProductASet = product?.type?.set
    const isProductABundle = product?.type?.bundle
    const {data} = useSitePreference('pdpVariationCategories')
    const pdpVariationCategory = data?.pdpVariationCategories

    const {variationAttributes} = useDerivedProduct(product, !!isProductASet, !!isProductABundle)

    // variationValues

    return (
        <Stack spacing="0" sx={{mb: '8px'}} {...props}>
            {variationAttributes &&
                variationAttributes.length > 0 &&
                variationAttributes?.map(({id, name, selectedValue}) => {
                    const variationName = pdpVariationCategory?.includes(
                        product?.primaryCategoryId || product?.c_primaryCatergoryID
                    )
                        ? name === 'Size'
                            ? intl.formatMessage({
                                  id: 'pdp.variation.waist.label',
                                  defaultMessage: 'Waist'
                              })
                            : name === 'Width'
                            ? intl.formatMessage({
                                  id: 'pdp.variation.length.label',
                                  defaultMessage: 'Length'
                              })
                            : name
                        : name
                    const content =
                        id === 'color'
                            ? selectedValue?.name
                            : sizeMappingData[selectedValue?.value] || selectedValue?.value
                    return (
                        <>
                            <Text key={`${variationName}: ${content}`} as="span" sx={variantValues}>
                                {/* {id === 'color' ? <></> : `${variationName}: ${content}`} */}
                                {name === 'Size' && product?.isPhysicalGiftCard ? (
                                    <>
                                        {'Amount: '}
                                        <FormattedNumber
                                            value={content}
                                            style="currency"
                                            currency={currency || activeCurrency || 'USD'}
                                        />
                                    </>
                                ) : (
                                    <>
                                        {variationName}: {content}
                                    </>
                                )}
                            </Text>
                        </>
                    )
                })}

            {/* quantity */}
            {isAccountPage && (
                <Text key={`quanity`} as="span" sx={variantValues}>
                    {'Qty'}: {quantity}
                </Text>
            )}

            {includeQuantity && (
                <Text as="span" sx={variantValues}>
                    <FormattedMessage
                        defaultMessage="Qty: {quantity}"
                        values={{quantity: variant.quantity}}
                        id="item_attributes.label.qty"
                    />
                </Text>
            )}
        </Stack>
    )
}

ItemAttributes.propTypes = {
    includeQuantity: PropTypes.bool,
    currency: PropTypes.string,
    product: PropTypes.object,
    isAccountPage: PropTypes.bool,
    quantity: PropTypes.number
}

export default ItemAttributes
